import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ButtonPrimary, ButtonSecondary } from "@/components/Buttons";
import { useAuth, formatApiError } from "@/context/AuthContext";
import api from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function VerifyEmail() {
    const [searchParams] = useSearchParams();
    const { refreshUser } = useAuth();
    const [status, setStatus] = useState("loading"); // loading | success | error
    const [message, setMessage] = useState("");
    const attempted = useRef(false);

    useEffect(() => {
        if (attempted.current) return;
        attempted.current = true;
        const token = searchParams.get("token");
        if (!token) {
            setStatus("error");
            setMessage("This verification link is missing its token.");
            return;
        }
        api.post("/auth/verify-email", { token })
            .then(async () => {
                setStatus("success");
                await refreshUser();
            })
            .catch((err) => {
                setStatus("error");
                setMessage(formatApiError(err));
            });
    }, [searchParams, refreshUser]);

    return (
        <div className="min-h-screen flex items-center justify-center px-6 pt-20" data-testid="verify-email-page">
            <div className="max-w-md w-full text-center bg-white border border-stone-200 rounded-sm px-8 py-14">
                {status === "loading" && (
                    <>
                        <Loader2 className="h-8 w-8 animate-spin text-gold mx-auto" />
                        <p className="mt-6 font-serif text-2xl font-light text-brand">Verifying your email…</p>
                    </>
                )}
                {status === "success" && (
                    <>
                        <CheckCircle2 className="h-10 w-10 text-gold mx-auto" strokeWidth={1.5} />
                        <h1 className="mt-6 font-serif text-3xl font-light text-brand" data-testid="verify-success">
                            Email verified
                        </h1>
                        <p className="mt-4 text-sm leading-relaxed text-mist">
                            Your Grayhaven Research account is now fully active. Your cart is saved exactly as you
                            left it — you can proceed straight to checkout.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                            <ButtonPrimary to="/cart" data-testid="verify-cart-btn">
                                Return to Cart
                            </ButtonPrimary>
                            <ButtonSecondary to="/materials" data-testid="verify-browse-btn">
                                Browse Research Materials
                            </ButtonSecondary>
                        </div>
                    </>
                )}
                {status === "error" && (
                    <>
                        <XCircle className="h-10 w-10 text-red-400 mx-auto" strokeWidth={1.5} />
                        <h1 className="mt-6 font-serif text-3xl font-light text-brand" data-testid="verify-error">
                            Verification failed
                        </h1>
                        <p className="mt-4 text-sm leading-relaxed text-mist">{message}</p>
                        <div className="mt-8">
                            <ButtonSecondary to="/" data-testid="verify-home-btn">
                                Return Home
                            </ButtonSecondary>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
